import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useAuth } from './AuthContext';

const useStrava = () => {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!user) { setConnected(false); setLoading(false); return; }
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const data = snap.data() || {};
      setConnected(!!data.stravaConnected);
      setLastSyncedAt(data.stravaLastSyncedAt?.toDate?.() || null);
    } catch (err) {
      console.error('Error reading Strava status:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const connectStrava = useCallback(async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    const idToken = await auth.currentUser.getIdToken();
    const res = await fetch('/api/strava/connect', {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.error || 'Strava connect failed');
    window.location.href = data.url;
  }, []);

  const syncActivities = useCallback(async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    setSyncing(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/api/strava/sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Strava sync failed');
      await refreshStatus();
      return data;
    } finally {
      setSyncing(false);
    }
  }, [refreshStatus]);

  return { connected, lastSyncedAt, loading, syncing, connectStrava, syncActivities };
};

export default useStrava;
